import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Menu, X } from "lucide-react";
import SiteVerification from "./SiteVerification";

export default function PublicLayout() {
  const [navPages, setNavPages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/pages/nav").then((r) => setNavPages(r.data || [])).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteVerification />
      <div className="bg-white border-b border-neutral-200/70" data-testid="logo-strip">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 sm:py-8 lg:py-10 flex justify-center">
          <Link to="/" aria-label="Spirit & Associates home" className="block group logo-frame">
            <picture>
              <source srcSet="/spirit-logo.webp" type="image/webp" />
              <img src="/spirit-logo.png" alt="Spirit & Associates" width="600" height="404" className="logo-img h-36 sm:h-44 lg:h-52 w-auto" fetchpriority="high" />
            </picture>
          </Link>
        </div>
      </div>
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-neutral-200/70" data-testid="public-header">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 h-16 flex items-center justify-center lg:justify-center">
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <nav className="flex items-center gap-5 xl:gap-7" data-testid="public-nav">
              {navPages.map((p) => (
                <NavLink
                  key={p.id}
                  to={`/${p.slug === "home" ? "" : p.slug}`}
                  end={p.slug === "home"}
                  className={({ isActive }) => `nav-link text-sm tracking-wide text-neutral-700 whitespace-nowrap ${isActive ? "active text-[#6B1F2A]" : ""}`}
                  data-testid={`nav-link-${p.slug}`}
                >
                  {p.nav_label || p.title}
                </NavLink>
              ))}
            </nav>

            <span className="w-px h-5 bg-neutral-300" aria-hidden />

            {settings?.primary_phone && (
              <a href={`tel:${settings.primary_phone}`} className="text-sm text-neutral-700 hover:text-[#6B1F2A] transition-colors whitespace-nowrap" data-testid="header-phone">
                {settings.primary_phone}
              </a>
            )}
            <Link to="/contact" className="btn-primary-burgundy" data-testid="header-cta">Get help</Link>
          </div>

          <div className="lg:hidden flex items-center justify-between w-full">
            <Link to="/" className="font-serif-display text-xl text-[#1a1a1a]">
              Spirit <span className="text-[#6B1F2A]">&</span> Associates
            </Link>
            <button className="text-neutral-700" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
            <nav className="flex flex-col px-6 py-4 gap-3">
              {navPages.map((p) => (
                <Link key={p.id} to={`/${p.slug === "home" ? "" : p.slug}`} className="py-2 text-neutral-700 hover:text-[#6B1F2A]">
                  {p.nav_label || p.title}
                </Link>
              ))}
              <Link to="/contact" className="btn-primary-burgundy mt-2 w-fit">Get help</Link>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-[#1a1a1a] text-white/80 mt-24" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <h3 className="font-serif-display text-2xl text-white mb-3">Spirit <span className="text-[#C49AA0]">&</span> Associates</h3>
            <p className="text-sm text-white/60 leading-relaxed">
              {settings?.tagline || "Special People Inspiring Recovery In Time"}
            </p>
          </div>
          <div>
            <p className="label-eyebrow text-white/60 mb-4">Interventions & Coaching</p>
            {settings?.primary_email && <p className="text-sm mb-2"><a href={`mailto:${settings.primary_email}`} className="hover:text-white">{settings.primary_email}</a></p>}
            {settings?.primary_phone && <p className="text-sm mb-2">{settings.primary_phone}</p>}
            {settings?.secondary_phone && <p className="text-sm">{settings.secondary_phone}</p>}
          </div>
          <div>
            <p className="label-eyebrow text-white/60 mb-4">5329 Evaluations</p>
            {settings?.evaluations_email && <p className="text-sm mb-2"><a href={`mailto:${settings.evaluations_email}`} className="hover:text-white">{settings.evaluations_email}</a></p>}
            {settings?.evaluations_phone && <p className="text-sm mb-2">Phone/Text: {settings.evaluations_phone}</p>}
            {settings?.fax && <p className="text-sm">Fax: {settings.fax}</p>}
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/50">© {new Date().getFullYear()} Spirit & Associates. {settings?.footer_text}</p>
            <Link to="/admin" className="text-xs text-white/40 hover:text-white" data-testid="admin-footer-link">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
